<script lang="ts" setup>
  import { SUP_PAYMENT_METHODS } from '@/enum/payments.ts'
  import checkIcon from '@/assets/icons/check.svg'
  import arrowLeft from '@assets/icons/arrow-left.svg'
  import type { PlanPaymentMethod, SubscriberUsingAttemptRequest, SubscriptionAttempt } from '@/types'
  import { getSubPaymentMethodName, handlePaymentErrors } from '@/utils/payments.ts'
  import { storeToRefs } from 'pinia'
  import axios from 'axios'
  import SubscriptionSuccess from '@/components/payment/SubscriptionSuccess.vue'
  import SubscriptionNoPlans from '@/components/payment/SubscriptionNoPlans.vue'
  import FeaturedPaymentForm from '@/components/payment/FeaturedPaymentForm.vue'
  import SinaProLogo from '@assets/logo/sina-pro.svg'
  import SinaProDarkLogo from '@assets/logo/sina-pro-v-dark.svg'

  import { useEventBus } from '@vueuse/core'
  import { useSubscriptionsStore } from '@stores/subscriptions.ts'
  import type { Coupon } from '@/types/Coupon.ts'
  import { useTheme } from 'vuetify/framework'
  import arrowUpIcon from '@assets/icons/arrow-up.svg'
  import arrowDownIcon from '@assets/icons/arrow-down.svg'

  const emit = defineEmits<{ close: [] }>()
  const excludedSubPaymentMethodIds: number[] = []
  const showList = ref(false)
  const router = useRouter()
  const subscriptionStore = useSubscriptionsStore()
  const loading = ref(false)
  const { error } = storeToRefs(subscriptionStore)
  const isActiveSubscriber = ref(false)
  const isInitializing = ref(true)
  const trackStore = useTrackStore()

  const { plans, planImages } = storeToRefs(subscriptionStore)

  const couponForm = subscriptionStore.getCouponForm()

  const { t } = useI18n()
  const snackbar = useSnackbar()

  const mainPlan = computed(() => {
    return plans.value?.[0]?.plan_payment_methods?.[0] as PlanPaymentMethod ?? undefined
  })


  const coupon = computed(() => {
    return mainPlan.value?.coupons?.[0] as Coupon ?? undefined
  })

  const selectedFeaturePlanPaymentMethod = ref<PlanPaymentMethod>()

  const countSubPaymentMethods = computed(() => {
    return plans.value.reduce((sum, plan) => sum + (plan.plan_payment_methods?.length || 0), 0)
  })

  const subscriptionBenefits = reactive([
    'subscription.benefits.one',
    'subscription.benefits.two',
    'subscription.benefits.three',
  ])

  const handleError = (e: unknown, context: string) => {
    console.error(`Error in ${context}:`, e)

    error.value = handlePaymentErrors(e)

    trackStore.track('payment_failed_presented', {
      payment_type: mainPlan.value?.sub_payment_method_id,
      error: error.value,
      version: trackStore.subscriptionVersion,
    })

    snackbar.showSnackbar({
      color: 'error',
      text: handlePaymentErrors(e),
      timeout: 5000,
    })
  }

  const clearError = () => {
    error.value = ''
  }

  const fetchPlans = async () => {
    try {
      //isInitializing.value = true
      clearError()

      if (typeof window.ApplePaySession !== 'function') {
        excludedSubPaymentMethodIds.push(SUP_PAYMENT_METHODS.applePay)
      }

      await subscriptionStore.fetchPlans(excludedSubPaymentMethodIds)

    } catch (error) {
      handleError(error, 'Fetch Plans')
    } finally {
      isInitializing.value = false
    }
  }


  watch(plans, val => {
    try {
      planImages.value = subscriptionStore.loadImages(val)
    } catch (error) {
      console.warn('Failed to load plan images:', error)
    }
  })

  watch(mainPlan, async value => {
    if (!value) {
      return
    }
    if (subscriptionBenefits.length === 4) {
      subscriptionBenefits.shift()
    }
    if (value?.plan?.consultation_amount) {
      subscriptionBenefits.unshift(t('subscription.benefits.consultation_amount', { consultation_amount: value.plan.consultation_amount }))
    }
  }, { immediate: true })

  const handleRetryAttempt = async (
    attempt: SubscriptionAttempt,
  ) => {
    try {
      loading.value = true
      clearError()

      await subscriptionStore.retryAttempt(attempt)
    } catch (e) {
      console.log('handleRetry error', e)
      handleError(e, 'Retry Attempt')

      if (axios.isAxiosError(error) && error?.response?.status && error?.response?.status >= 500) {
        await router.push({
          name: 'subscriptions-failed',
          query: { message: error.response.data.message, sub_payment_method_id: attempt.sub_payment_method_id },
        })
      }
    } finally {
      loading.value = false
    }
  }

  const handlePaymentCompleted = async (
    attempt: SubscriptionAttempt,
    payload: SubscriberUsingAttemptRequest
  ) => {
    try {
      clearError()
      loading.value = true

      await subscriptionStore.createSubscription(attempt, payload)
      isActiveSubscriber.value = true
      snackbar.showSnackbar({
        color: 'success',
        text: t('subscription.payment_successful'),
        timeout: 3000,
      })

      const createSubscriptionBus = useEventBus('subscription-created')
      createSubscriptionBus.emit()

    } catch (e) {
      console.log('error', e)
      handleError(e, 'Create Subscription')

      if (axios.isAxiosError(error) && error?.response?.status && error?.response?.status >= 500) {
        await router.push({
          name: 'subscriptions-failed',
          query: { message: error.response.data.message, sub_payment_method_id: attempt.sub_payment_method_id },
        })
      }
    } finally {
      loading.value = false
    }
  }

  const handleShowPaymentList = () => {
    selectedFeaturePlanPaymentMethod.value = undefined
    showList.value = true
    trackStore.track('other_payment_methods_clicked', {
      version: trackStore.subscriptionVersion,
    })
  }

  const openSelectedFeaturedPlanList = () => {
    selectedFeaturePlanPaymentMethod.value = mainPlan.value
    showList.value = true
  }

  const handlePaymentError = (error: unknown) => {
    snackbar.showSnackbar({
      color: 'error',
      text: handlePaymentErrors(error),
      timeout: 5000,
    })
  }

  const onClose = () => {
    emit('close')
  }

  const verifyCoupon = (errorCallback = () => {
  }) => {
    couponForm.validate({
      only: ['filter.coupon_code'],
      onSuccess: () => {
        subscriptionStore.setCouponCode(couponForm.filter.coupon_code)
        trackStore.track('coupon_applied', {
          coupon_name: couponForm.filter.coupon_code,
        })
        fetchPlans()
      },
      onValidationError: () => {
        if(typeof errorCallback === 'function') {
          errorCallback()
        }
      },

    })
  }
  const removeCoupon = () => {
    couponForm.reset()
    subscriptionStore.setCouponCode(undefined)
    fetchPlans()
  }


  if (window.settings.public_coupon) {
    couponForm.filter.coupon_code = window.settings.public_coupon
    verifyCoupon(() => {
      couponForm.reset()
      couponForm.forgetError('filter.coupon_code')
      fetchPlans()
    })
  } else {
    fetchPlans()
  }

</script>

<template>
  <v-container
    class="bg-background py-md-4 overflow-y-auto position-relative"
    :class="[
      `rounded-${$vuetify.display.mdAndDown? '0' : 'xl'}`
    ]"
    fluid
    :height="$vuetify.display.mdAndDown ? '100dvh' : '773px'"
  >
    <v-overlay
      v-if="isInitializing"
      class="align-center justify-center"
      persistent
    >
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      />
      <p class="mt-4 text-white">{{ t('subscription.loading') }}</p>
    </v-overlay>

    <!-- Subscribed -->
    <subscription-success v-else-if="isActiveSubscriber" @close="onClose" />

    <!-- No plans -->
    <subscription-no-plans
      v-else-if="!plans.length"
      :loading="loading"
      @close="onClose"
      @fetch-plans="() => couponForm.filter.coupon_code ? removeCoupon() : fetchPlans()"
    />

    <!-- Plans list -->
    <template v-else>
      <v-row
        v-if="showList"
        class="flex-grow-1"
        height="100dvh"
        justify="center"
        no-gutters
      >
        <v-col cols="12">
          <plans-list
            :plans="plans"
            :pre-selected-plan-payment-method="selectedFeaturePlanPaymentMethod"
            @back="showList = false"
            @create-payment="handlePaymentCompleted"
            @retry-attempt="handleRetryAttempt"
          />
        </v-col>
      </v-row>
      <template v-else>
        <subscription-header class="mb-3" @close="onClose">
          <v-col align-self="center" class="mt-10 mt-md-6 d-flex justify-center text-grey-100">
            <sina-pro-dark-logo
              v-if="useTheme().global.name.value === 'dark'"
              height="83"
              width="80"
            />
            <sina-pro-logo
              v-else
              height="83"
              width="80"
            />
          </v-col>
        </subscription-header>
        <h1 class="text-grey-50 text-h6 font-weight-bold mt-2 mb-3 px-8 px-md-5 text-center">
          {{ t('subscription.title') }}
        </h1>
        <v-slide-y-transition group>
          <v-row
            v-if="coupon"
            key="coupon-row"
            class="my-3 my-md-5"
            justify="center"
            no-gutters
          >
            <v-col
              cols="12"
              lg="11"
              md="6"
              sm="12"
            >
              <div class="d-flex px-4 py-4 ga-3 bg-brand-950 rounded-sm ">
                <v-avatar color="primary" rounded="sm">
                  <v-icon color="white" icon="mdi-ticket-confirmation-outline" size="24" />
                </v-avatar>
                <div class="flex-grow-1">
                  <p class="text-grey-50 text-body-2 font-weight-bold">
                    {{ coupon.title }}
                  </p>
                  <p class="text-grey-400 text-caption font-weight-medium">
                    {{ coupon.description }}
                  </p>
                </div>
                <v-icon color="secondary" icon="mdi-close" size="16" @click="removeCoupon" />
              </div>
            </v-col>
          </v-row>
          <v-row key="details-row" justify="center" no-gutters>
            <v-col
              cols="12"
              lg="11"
              md="6"
              sm="12"
            >
              <v-card
                class="mx-auto pa-5 bg-grey-900 border-sm border-grey-800 border-opacity-100 mt-3"
                flat
                rounded="lg"
              >
                <v-card-text class="pa-0">
                  <div class="font-weight-bold text-grey-50 d-flex align-center ga-1">
                    <template v-if="coupon?.pivot && coupon?.type== 'percentage' ">
                      <span class="font-weight-bold text-h5 text-grey-600 text-decoration-line-through">
                        {{ (mainPlan?.price_local).toFixed(2) }}
                      </span>
                      <span class="font-weight-bold text-h5">
                        {{ (coupon.pivot!.price_local).toFixed(2) }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="font-weight-bold text-h5">
                        {{ (mainPlan?.price_local).toFixed(2) }}
                      </span>
                    </template>

                    <span class="text-grey-300 font-weight-medium text-subtitle-2 flex-grow-1 align-self-end mb-1">
                      {{
                        t('plan_price_details', {
                          price: t(`subscription.currencies.${mainPlan?.currency}`),
                          duration: t(`subscription.${mainPlan?.plan?.is_recurring ? 'is_recurring' : 'not_recurring'}.durations.${mainPlan?.duration}`),
                        })
                      }}
                    </span>
                    <v-chip v-if="coupon" class="text-white bg-red text-body-2 font-weight-medium" rounded="sm">
                      {{
                        coupon.type == 'free' ? t('coupon_discount_free') : t('coupon_discount_percentage', {percentage: coupon.percentage})
                      }}
                    </v-chip>
                  </div>
                  <p class="text-caption text-grey-500">
                    <i18n-t
                      :keypath="plans?.[0]?.is_recurring ? 'subscription.recurring_plan' : 'subscription.one_time_plan'"
                    />
                  </p>

                  <v-list
                    class="mt-3"
                    density="compact"
                    lines="one"
                    slim
                    style="background-color: inherit"
                  >
                    <v-list-item
                      v-for="subscriptionBenefit in subscriptionBenefits"
                      :key="subscriptionBenefit"
                      class="mb-2 pa-0"
                      density="compact"
                      min-height="auto"
                      slim
                    >
                      <div class="d-flex align-center ga-2">
                        <v-icon color="grey-400" :icon="checkIcon" />
                        <v-list-item-title class="text-subtitle-2 text-grey-100">
                          <i18n-t :keypath="subscriptionBenefit" />
                        </v-list-item-title>
                      </div>
                    </v-list-item>
                  </v-list>

                  <div class="mt-1">
                    <suspense>
                      <featured-payment-form
                        :key="mainPlan?.id+'|'+coupon?.code"
                        :plan-payment-method="mainPlan"
                        @open-plan-list="openSelectedFeaturedPlanList"
                        @payment-completed="handlePaymentCompleted"
                        @payment-error="handlePaymentError"
                        @retry-attempt="handleRetryAttempt"
                      />
                      <template #fallback>
                        <payment-skeleton-loader
                          :type="getSubPaymentMethodName(mainPlan?.sub_payment_method_id ?? 1)"
                        />
                      </template>
                    </suspense>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row
            v-if="!coupon"
            key="add-coupon"
            class="mt-3 mt-md-5"
            justify="center"
            no-gutters
          >
            <v-col
              cols="12"
              lg="11"
              md="6"
              sm="12"
            >
              <v-card
                border="sm grey-800 opacity-100 dashed"
                class="cursor-pointer flat bg-transparent px-0"
                flat
                :hover="false"
                :ripple="false"
                rounded="lg"
              >
                <v-expansion-panels>
                  <v-expansion-panel color="background">
                    <v-expansion-panel-title
                      class="px-4 ga-3"
                      :collapse-icon="arrowUpIcon"
                      :expand-icon="arrowDownIcon"
                    >
                      <v-avatar color="brand-950" rounded="sm">
                        <v-icon color="primary" icon="mdi-ticket-confirmation-outline" size="24" />
                      </v-avatar>

                      <div>
                        <p class="text-grey-50 text-body-2 font-weight-bold">
                          {{ t('do_you_have_discount_code') }}
                        </p>
                        <p class="text-grey-400 text-caption font-weight-medium">
                          {{ t('enter_coupon_now_to_activate_discount') }}
                        </p>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="bg-background">
                      <div class="d-flex flex-row ga-2">
                        <v-text-field
                          v-model="couponForm.filter.coupon_code"
                          color="grey-600"
                          density="compact"
                          :error="couponForm.invalid('filter.coupon_code')"
                          :error-messages="couponForm.errors['filter.coupon_code']"
                          hide-details="auto"
                          :placeholder="t('enter_coupon_code_here')"
                          rounded="md"
                          variant="outlined"
                          @change="couponForm.forgetError( 'filter.coupon_code')"
                        />

                        <v-fade-transition>
                          <v-btn
                            v-if="couponForm.filter.coupon_code?.length"
                            border="primary"
                            class="font-weight-bold"
                            color="primary"
                            :disabled="couponForm.validating"
                            size="large"
                            variant="outlined"
                            @click="verifyCoupon"
                          >
                            {{ t('verify_coupon') }}
                          </v-btn>
                        </v-fade-transition>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </v-col>
          </v-row>
        </v-slide-y-transition>


        <v-row
          v-if="countSubPaymentMethods > 1"
          class="mt-3 mt-md-5"
          justify="center"
          no-gutters
        >
          <v-col
            cols="12"
            lg="11"
            md="6"
            sm="12"
          >
            <v-card
              class="cursor-pointer flat pa-4 bg-transparent border-sm border-grey-800 border-opacity-100"
              flat
              rounded="lg"
              variant="text"
              @click="handleShowPaymentList"
            >
              <v-card-text class="d-flex align-center justify-space-between pa-0 ga-4">
                <div>
                  <p class="text-grey-50 text-subtitle-2">
                    <i18n-t keypath="subscription.list_card.title" />
                  </p>

                  <div class="text-caption text-grey-400 mb-3">
                    <i18n-t keypath="subscription.list_card.description" />
                  </div>

                  <div class="d-flex align-center flex-wrap ga-1">
                    <v-img
                      v-for="planImage in planImages"
                      :key="planImage"
                      class="flex-0-1"
                      contain
                      height="18"
                      max-width="40"
                      :src="planImage"
                      width="auto"
                    />
                  </div>
                </div>

                <v-icon color="grey-500" :icon="arrowLeft" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>
  </v-container>
</template>

<style lang="scss" scoped>
.price {
  letter-spacing: -1.95px;
}

.text-medium {
  font-size: 1.125rem;
}

.empty-state {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
